import cn from 'classnames';
import dynamic from 'next/dynamic';
// Types
import { GetMediaPost_getMediaPost } from 'api/mediaPost/types/GetMediaPost';
import { GetMediaPosts_getMediaPosts_entities } from 'api/mediaPost/types/GetMediaPosts';
// Hooks
import { useGetAthleteMemorabilia } from 'hooks';
// Routes
import { MERCH, PRODUCTS } from 'constants/routes';
// Components
import MediaFeedItemVideo from './MediaFeedItemVideo/MediaFeedItemVideo';
import MediaFeedItemImage from './MediaFeedItemImage/MediaFeedItemImage';
import Section from 'components/common3/Section/Section';
// Direct import (not next/dynamic) so getDataFromTree picks up the inner
// useQuery during SSR and the "Other Media Posts" links land in the initial
// HTML — helps Google internal-link discovery + non-JS crawlers.
import MediaFeedList from 'components/ProfileSetup/components/ChannelCreation/MediaFeedList/MediaFeedList';
// Styles
import styles from './MediaFeedItem.module.scss';
import MediaFeedNavigation from './MediaFeedNavigation/MediaFeedNavigation';
const RelatedProfileProductsList = dynamic(
  import('../RelatedProfileProductsList/RelatedProfileProductsList'),
  {
    ssr: false,
  }
);

type MediaFeedContainerProps = {
  mediaFeed: GetMediaPost_getMediaPost;
  // Pre-fetched related posts from getInitialProps. Lets the "Other Media
  // Posts" carousel ship in the SSR HTML without a second sequential server
  // roundtrip per request.
  initialRelatedPosts?: GetMediaPosts_getMediaPosts_entities[] | null;
};

const MediaFeedItem = ({
  mediaFeed,
  initialRelatedPosts,
}: MediaFeedContainerProps) => {
  const { data: memorabiliaData } = useGetAthleteMemorabilia();

  const memorabilia = memorabiliaData?.getMemorabilia.entities || [];
  const hasProducts = !!memorabilia.length;
  const isVideo = 'video' in mediaFeed;
  const storeId = mediaFeed.store.id || '';
  const shouldShowProductsSection = mediaFeed.store.hasProducts && !isVideo;
  const seeAllLink = hasProducts
    ? `${MERCH}?athleteIds=${storeId}`
    : `${PRODUCTS}?athleteIds=${storeId}`;

  const seeAllMediaLink = `/${mediaFeed.store?.slug}/media`;

  if (!storeId) {
    return null;
  }

  return (
    <>
      <Section title="" backgroundTitle="Media" className={styles.section}>
        <>
          {isVideo ? (
            <MediaFeedItemVideo mediaFeed={mediaFeed} />
          ) : (
            <>
              <MediaFeedNavigation mediaFeed={mediaFeed} />
              <MediaFeedItemImage mediaFeed={mediaFeed} />
            </>
          )}
        </>
      </Section>
      <Section
        title=""
        className={cn(styles.section, styles.relatedMediaPostsSection)}
      >
        <MediaFeedList
          customTitle="Other Media Posts"
          storeId={storeId}
          storeSlug={mediaFeed.store?.slug || undefined}
          mediaFeedId={mediaFeed?.id}
          className={styles.mediaFeedList}
          allMediaUrl={seeAllMediaLink}
          fetchPolicy="cache-first"
          initialEntities={initialRelatedPosts}
        />
      </Section>
      {shouldShowProductsSection && (
        <Section
          title="Featured Products"
          seeAllLink={seeAllLink}
          seeAllLinkTitle="View Shop"
          className={cn(styles.section, styles.relatedMediaPostsSection)}
        >
          <RelatedProfileProductsList
            className={styles.relatedProducts}
            storeId={storeId || ''}
            shouldHideMostExpensiveItem
            isProductDetailsPage
          />
        </Section>
      )}
    </>
  );
};

export default MediaFeedItem;
